import EmptyState from '@/components/EmptyState';
import { useGiftCards, useIssueGiftCard } from '@/features/gift-cards/hooks/useGiftCards';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { inputToMinor, formatMoney } from '@/utils/money';
import type { GiftCard } from '@restaurant/api-client';
import {
  Badge,
  Block,
  BlockTitle,
  Button,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Link as F7Link,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useState } from 'react';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'green',
  DEPLETED: 'gray',
  VOID: 'red',
  EXPIRED: 'orange',
};

function GiftCardsPage() {
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: cards, isLoading, error } = useGiftCards();
  const issue = useIssueGiftCard();
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';

  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [recipientName, setRecipientName] = useState('');

  const doIssue = () => {
    const initialBalanceCents = amount.trim() ? inputToMinor(amount, currency) : 0;
    if (initialBalanceCents <= 0) {
      f7.toast.show({ text: 'Enter a starting balance.', closeTimeout: 2500, position: 'bottom' });
      return;
    }
    issue.mutate(
      { initialBalanceCents, recipientName: recipientName.trim() || undefined },
      {
        onSuccess: (card) => {
          f7.toast.show({
            text: `Gift card ${card.code} issued`,
            closeTimeout: 3000,
            position: 'bottom',
          });
          setShowForm(false);
          setAmount('');
          setRecipientName('');
        },
      },
    );
  };

  const list = cards ?? [];

  return (
    <Page name="gift-cards">
      <Navbar title="Gift cards" backLink={settingsBackLink}>
        <NavRight>
          <F7Link iconF7="plus" onClick={() => setShowForm((v) => !v)} />
        </NavRight>
      </Navbar>

      {showForm && (
        <>
          <BlockTitle>Issue a gift card</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={`Starting balance (${currency})`}
              type="text"
              placeholder="50.00"
              value={amount}
              onInput={(e) => setAmount(e.target.value)}
            />
            <ListInput
              label="Recipient name (optional)"
              type="text"
              placeholder="Jane Smith"
              value={recipientName}
              onInput={(e) => setRecipientName(e.target.value)}
            />
          </List>
          <Block>
            <Button fill large onClick={doIssue} disabled={issue.isPending}>
              {issue.isPending ? 'Issuing…' : 'Issue card'}
            </Button>
          </Block>
        </>
      )}

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {error && (
        <EmptyState
          icon="exclamationmark_triangle"
          iconColor="#ff9500"
          title="Couldn’t load gift cards"
          description={(error as Error).message}
        />
      )}

      {cards && list.length === 0 && !showForm && (
        <EmptyState
          icon="creditcard"
          title="No gift cards yet"
          description="Issue a prepaid gift card a guest can redeem against a dine-in bill. Tap +."
        />
      )}

      {list.length > 0 && (
        <List strongIos strongMd outlineIos outlineMd dividersIos dividersMd mediaList>
          {list.map((c: GiftCard) => (
            <ListItem
              key={c.id}
              link={`/settings/gift-cards/${c.id}`}
              title={c.code}
              subtitle={`${formatMoney(c.balanceCents, c.currency)} of ${formatMoney(c.initialBalanceCents, c.currency)}`}
              text={c.recipientName ?? undefined}
            >
              <div slot="after">
                <Badge color={STATUS_COLOR[c.status] ?? 'gray'}>{c.status}</Badge>
              </div>
            </ListItem>
          ))}
        </List>
      )}
    </Page>
  );
}

export default GiftCardsPage;
